import React, { useContext, useEffect, useMemo, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./ListaMaquinas.css";
import { Button, Col, Row } from "antd";
import axios from "axios";
import * as links from "../../../../utils/links";
import { AuthContext } from "../../../../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import LoadingAction from "../../../../themes/LoadingAction/LoadingAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faCheckCircle, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const ListaMaquinas = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const { dataUser } = authInfo;
  const navigate = useNavigate();
  const token = authInfo?.dataUser?.token;

  const [maquinasOnline, setMaquinasOnline] = useState([]);
  const [maquinasOffline, setMaquinasOffline] = useState([]);
  const [maquinasInfos, setMaquinasInfos] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("TODAS");
  const [filtroEmpresa, setFiltroEmpresa] = useState("TODAS");
  const [gruposFechados, setGruposFechados] = useState({});

  const nomeEmpresa = (maquina) =>
    (maquina?.clienteNome || "SEM EMPRESA").trim() || "SEM EMPRESA";

  const todas = useMemo(
    () => [
      ...maquinasOnline.map((m) => ({ ...m, __tipo: "online" })),
      ...maquinasOffline.map((m) => ({ ...m, __tipo: "offline" })),
    ],
    [maquinasOnline, maquinasOffline]
  );

  const empresas = useMemo(
    () => Array.from(new Set(todas.map(nomeEmpresa))).sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" })),
    [todas]
  );

  const maquinasFiltradas = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase("pt-BR");
    return todas.filter((m) => {
      const empresa = nomeEmpresa(m);
      if (filtroEmpresa !== "TODAS" && empresa !== filtroEmpresa) return false;
      if (filtroStatus === "ONLINE" && m.__tipo !== "online") return false;
      if (filtroStatus === "OFFLINE" && m.__tipo !== "offline") return false;
      if (filtroStatus === "SINAL_FRACO" && !(m.__tipo === "online" && Number(m.nivelDeSinal || 0) > 0 && Number(m.nivelDeSinal || 0) <= 30)) return false;
      if (!termo) return true;
      const texto = [empresa, m.nome, m.descricao, m.store_id, m.maquininha_serial, m.id]
        .map((v) => String(v || ""))
        .join(" ")
        .toLocaleLowerCase("pt-BR");
      return texto.includes(termo);
    });
  }, [todas, busca, filtroStatus, filtroEmpresa]);

  const empresasAgrupadas = useMemo(() => {
    const grupos = new Map();
    maquinasFiltradas.forEach((maquina) => {
      const empresa = nomeEmpresa(maquina);
      if (!grupos.has(empresa)) grupos.set(empresa, { empresa, online: [], offline: [] });
      grupos.get(empresa)[maquina.__tipo].push(maquina);
    });
    return Array.from(grupos.values()).sort((a, b) =>
      a.empresa.localeCompare(b.empresa, "pt-BR", { sensitivity: "base" })
    );
  }, [maquinasFiltradas]);

  const dataData = () => {
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/machines-adm`, {
        headers: { "x-access-token": token, "content-type": "application/json" },
      })
      .then((res) => {
        if (res.status !== 200) throw new Error("Falha ao carregar máquinas");
        setMaquinasOnline(Array.isArray(res.data?.maquinasOnline) ? res.data.maquinasOnline : []);
        setMaquinasOffline(Array.isArray(res.data?.maquinasOffline) ? res.data.maquinasOffline : []);
        setMaquinasInfos(res.data || {});
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        const status = err.response?.status;
        if ([401, 403].includes(status)) {
          setNotiMessage({ type: "error", message: "A sua sessão expirou, para continuar faça login novamente." });
          navigate(dataUser?.key === "ADMIN" ? links.ADMIN_SIGNIN : links.SIGNIN);
          setDataUser(null);
          return;
        }
        setNotiMessage({ type: "error", message: "Não foi possível atualizar a lista de máquinas." });
      });
  };

  useEffect(() => {
    dataData();
    const intervalId = setInterval(dataData, 60000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderSinal = (post, estaOnline) => {
    const sinal = Number(post?.nivelDeSinal || 0);
    let classe = "bi-reception-0";
    if (sinal > 0 && sinal <= 30) classe = "bi-reception-1";
    else if (sinal <= 60 && sinal > 30) classe = "bi-reception-2";
    else if (sinal <= 70 && sinal > 60) classe = "bi-reception-3";
    else if (sinal > 70) classe = "bi-reception-4";
    return (
      <OverlayTrigger key={`sinal-${post.id}`} placement="top" overlay={<Tooltip id={`tooltip-top-${post.id}`}>{estaOnline ? `${sinal}%` : "Offline"}</Tooltip>}>
        <i className={`bi ${classe}`} style={{ fontSize: "42px", color: estaOnline && sinal > 0 ? "blue" : "transparent" }} />
      </OverlayTrigger>
    );
  };

  const renderMaquina = (post) => {
    const status = String(post.status || "").toUpperCase();
    const pagamentoRecente = status === "PAGAMENTO_RECENTE";
    const estaOnline = post.__tipo === "online" || status === "ONLINE" || pagamentoRecente;
    const ultima = post.ultimaRequisicao ? new Date(post.ultimaRequisicao).toLocaleString("pt-BR") : "Sem registro";
    return (
      <Col xs={24} md={24} lg={8} xl={8} className="Cliente_Maquinas_col" key={post.id}>
        <div className="maquina maquina-empresa-card">
          <div className="maquina-info">
            <div className="maquina-status-topo">
              <FontAwesomeIcon icon={estaOnline ? faCheckCircle : faXmarkCircle} color={pagamentoRecente ? "blue" : estaOnline ? "green" : "red"} className="logout-icon fa-3x" />
              {renderSinal(post, estaOnline)}
            </div>
            <h2>{post.nome}</h2>
            <h6 style={{ fontWeight: "300" }}>{post.status} - {post.descricao}</h6>
            <h6 style={{ fontWeight: "300" }}>MP: {post.store_id}</h6>
            <h6 style={{ fontWeight: "300" }}>PAG: {post.maquininha_serial}</h6>
            <h6 style={{ fontWeight: "300" }}>Cliente: {post.clienteNome}</h6>
            <h6 className="maquina-ultima-comunicacao">Última comunicação: {ultima}</h6>
          </div>
        </div>
      </Col>
    );
  };

  const renderEmpresa = ({ empresa, online, offline }) => {
    const fechado = gruposFechados[empresa] ?? true;
    return (
      <section className="empresa-maquinas-grupo" key={empresa}>
        <div className="empresa-maquinas-cabecalho">
          <div className="empresa-maquinas-identidade">
            <span className="empresa-maquinas-legenda">EMPRESA</span>
            <h5>{empresa}</h5>
          </div>
          <div className="empresa-maquinas-totais">
            <div className="empresa-maquinas-contadores">
              <span className="empresa-maquinas-total online">{online.length} ONLINE</span>
              <span className="empresa-maquinas-total offline">{offline.length} OFFLINE</span>
            </div>
            <button
              className="empresa-recolher"
              type="button"
              aria-label={fechado ? `Abrir ${empresa}` : `Recolher ${empresa}`}
              title={fechado ? "Abrir" : "Recolher"}
              onClick={() => setGruposFechados((v) => ({ ...v, [empresa]: !fechado }))}
            >
              <i className={`bi ${fechado ? "bi-chevron-down" : "bi-chevron-up"}`}></i>
            </button>
          </div>
        </div>
        {!fechado && (
          <>
            {online.length > 0 && <><div className="empresa-subtitulo online">ONLINE</div><Row className="empresa-maquinas-row">{online.map(renderMaquina)}</Row></>}
            {offline.length > 0 && <><div className={`empresa-subtitulo offline ${online.length ? "com-divisao" : ""}`}>OFFLINE</div><Row className="empresa-maquinas-row">{offline.map(renderMaquina)}</Row></>}
          </>
        )}
      </section>
    );
  };

  const limparFiltros = () => { setBusca(""); setFiltroStatus("TODAS"); setFiltroEmpresa("TODAS"); };

  return (
    <div className="Cliente_Maquinas_container">
      {isLoading && <LoadingAction />}
      <div className="WarningMsgSpan"><span>{dataUser?.warningMsg}</span></div>
      <div className="AddCliente_header"><div className="AddCliente_header_title">Máquinas:</div><Link className="AddCliente_header_back" to={links.DASHBOARD_CLIENTES}>VOLTAR</Link></div>
      <div className="Cliente_Maquinas_action"><Button style={{ margin: "0 15px" }} onClick={dataData}><FontAwesomeIcon icon={faArrowsRotate} style={{ marginRight: "5px" }} />Atualizar</Button></div>

      <div className="maquinas-resumo-geral">
        <div><span>Total</span><strong>{maquinasInfos?.total ?? 0}</strong></div>
        <div className="resumo-online"><span>Online</span><strong>{maquinasInfos?.online ?? 0}</strong></div>
        <div className="resumo-offline"><span>Offline</span><strong>{maquinasInfos?.offline ?? 0}</strong></div>
      </div>

      <div className="maquinas-filtros">
        <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar máquina, empresa, MP, PAG ou ID" />
        <select value={filtroEmpresa} onChange={(e) => setFiltroEmpresa(e.target.value)}><option value="TODAS">Todas as empresas</option>{empresas.map((e) => <option key={e} value={e}>{e}</option>)}</select>
        <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}><option value="TODAS">Todos os status</option><option value="ONLINE">Online</option><option value="OFFLINE">Offline</option><option value="SINAL_FRACO">Sinal fraco</option></select>
        <button type="button" onClick={limparFiltros}>Limpar</button>
      </div>
      <div className="maquinas-resultado-filtro">Exibindo {maquinasFiltradas.length} de {todas.length} máquinas</div>

      <div className="maquinas-bloco-titulo"><h6>Máquinas por empresa:</h6></div>
      <div className="empresas-maquinas-lista">
        {empresasAgrupadas.length > 0 ? empresasAgrupadas.map(renderEmpresa) : <div className="empresa-sem-maquinas">Nenhuma máquina encontrada com esses filtros.</div>}
      </div>
    </div>
  );
};

export default ListaMaquinas;
